import {REVIEW_POV_OPTIONS} from './reviewPovOptions';
import useLiveSetupFocusPoints from "../../utils/useLiveSetupFocusPoints";
import {reportOperationNotification} from '../../utils/operationNotifications';
import VodMarkerPicker from './VodMarkerPicker';
import { updatePendingVodUpload, removePendingVodUpload } from "./pendingVodUploads";
import {noteClassification} from '../../utils/noteClassification';
// E:\new-react-project\the-ultimate-guide\src\components\Review\UploadVideoModal.js
import React, { useState, useMemo, useEffect, useRef } from "react";
import { db } from "../../Security/firebase-config";
import {
  collection,
  doc,
  Timestamp,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  writeBatch,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";
import { useTeam } from "../../context/TeamContext";
import CustomAlert from "../core-components/CustomAlert";
import { ensureActiveTeamClaim } from "../../utils/authClaims";
import UploadScoreDialog from "../../core-pages/team-stats/UploadScoreDialog";
import {
  checkTeamQuotaAllowance,
  confirmTrackedUploadFinalize,
  triggerTeamQuotaRefresh,
} from "../../utils/teamQuota";
import {beginOversizedVideoProcessing} from "../../utils/videoProcessing";
import {
  linkMatchToCalendarEvent,
  toLocalDateTimeInputs,
} from "../../utils/calendarMatchLink";
import {
  buildMatchReservationKey,
  claimReservedMatch,
  cleanupStaleMatchReservations,
  createMatchReservationSessionId,
  ensureMatchReservation,
  isReservedMatchDoc,
  markMatchReservationStarted,
  refreshMatchReservationHeartbeat,
  releaseMatchReservation,
  getCurrentLocalDateInputValue,
} from "../../utils/matchReservations";
import FocusPoints from "./FocusPoints";
import CalendarMatchLinker from "../planning/CalendarMatchLinker";
import {readSetupSelection} from "../../utils/eventSetupStore";
import useEventSetupLifecycle from "../planning/useEventSetupLifecycle";

import "./UploadVideoModal.css";

const MAX_UPLOAD_WIDTH = 1920;
const MAX_UPLOAD_HEIGHT = 1080;
const MAX_UPLOAD_FPS = 60;
const SOURCE_UPLOAD_PREFIX = "video-staging";
const RENDER_OUTPUT_PREFIX = "video-rendered";

function normalizeEvenDimension(value, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  const even = Math.floor(numeric / 2) * 2;
  return Math.max(2, even || fallback);
}

function computeCappedVideoTarget(meta) {
  const sourceWidth = Number(meta?.width || 0);
  const sourceHeight = Number(meta?.height || 0);

  if (!sourceWidth || !sourceHeight) {
    return {
      width: MAX_UPLOAD_WIDTH,
      height: MAX_UPLOAD_HEIGHT,
    };
  }

  const scale = Math.min(
    1,
    MAX_UPLOAD_WIDTH / sourceWidth,
    MAX_UPLOAD_HEIGHT / sourceHeight
  );

  return {
    width: normalizeEvenDimension(sourceWidth * scale, MAX_UPLOAD_WIDTH),
    height: normalizeEvenDimension(sourceHeight * scale, MAX_UPLOAD_HEIGHT),
  };
}

function estimateProcessingSecondsForUpload(meta, fileSizeBytes) {
  const sourceDurationSec = Math.max(0, Number(meta?.durationSec || 0));
  const sourceBytes = Math.max(0, Number(fileSizeBytes || 0));
  const bytesGb = sourceBytes / (1024 ** 3);

  return Math.round(
    Math.max(
      60,
      Math.min(
        1800,
        (sourceDurationSec * 0.5) +
          (bytesGb * 90) +
          45
      )
    )
  );
}

function normalizePovName(value) {
  return String(value || "").trim().toLowerCase();
}

function buildCloudPovOptionLabel(option, existsInCloud) {
  return existsInCloud ? `${option} | Already exists in the Cloud` : option;
}

function extractUniquePovNamesFromSnapshot(snapshot) {
  return Array.from(
    new Set(
      snapshot.docs
        .map((docSnap) => {
          const povName = docSnap.data()?.povName;
          return typeof povName === "string" ? povName.trim() : "";
        })
        .filter(Boolean)
    )
  );
}

async function estimateVideoFrameRate(videoEl) {
  if (
    !videoEl ||
    typeof videoEl.requestVideoFrameCallback !== "function" ||
    !Number.isFinite(videoEl.duration) ||
    videoEl.duration <= 0
  ) {
    return null;
  }

  const originalTime = Number(videoEl.currentTime || 0);
  const sampleTime = Math.max(
    0,
    Math.min(videoEl.duration - 0.5, videoEl.duration * 0.15)
  );

  const previousMuted = videoEl.muted;
  const previousPlaybackRate = videoEl.playbackRate;
  const previousVolume = videoEl.volume;
  let timer = null;

  try {
    videoEl.muted = true;
    videoEl.volume = 0;
    videoEl.playbackRate = 1;
    videoEl.currentTime = sampleTime;

    await new Promise((resolve) => {
      const onSeeked = () => {
        videoEl.removeEventListener("seeked", onSeeked);
        resolve();
      };
      videoEl.addEventListener("seeked", onSeeked);
    });

    let frameCount = 0;
    let firstMediaTime = null;
    let lastMediaTime = null;

    const fps = await new Promise((resolve) => {
      const finish = (value) => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        resolve(value);
      };

      timer = setTimeout(() => finish(null), 1400);

      const onFrame = (_now, metadata) => {
        frameCount += 1;
        if (firstMediaTime === null) firstMediaTime = metadata.mediaTime;
        lastMediaTime = metadata.mediaTime;

        if (frameCount >= 40 || metadata.mediaTime - firstMediaTime >= 0.75) {
          const elapsedMedia = Math.max(0.001, lastMediaTime - firstMediaTime);
          finish(frameCount / elapsedMedia);
          return;
        }

        videoEl.requestVideoFrameCallback(onFrame);
      };

      videoEl.play()
        .then(() => videoEl.requestVideoFrameCallback(onFrame))
        .catch(() => finish(null));
    });

    return Number.isFinite(fps) ? Number(fps.toFixed(2)) : null;
  } catch {
    return null;
  } finally {
    try {
      videoEl.pause();
      videoEl.currentTime = originalTime;
    } catch {}

    videoEl.muted = previousMuted;
    videoEl.playbackRate = previousPlaybackRate;
    videoEl.volume = previousVolume;
  }
}

async function inspectSelectedVideoFile(file) {
  if (!file) return null;

  const objectUrl = URL.createObjectURL(file);
  const videoEl = document.createElement("video");
  videoEl.preload = "metadata";
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.src = objectUrl;

  try {
    await new Promise((resolve, reject) => {
      const onLoadedMetadata = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        reject(new Error("Could not read the selected video metadata."));
      };

      const cleanup = () => {
        videoEl.removeEventListener("loadedmetadata", onLoadedMetadata);
        videoEl.removeEventListener("error", onError);
      };

      videoEl.addEventListener("loadedmetadata", onLoadedMetadata);
      videoEl.addEventListener("error", onError);
    });

    const fpsEstimate = await estimateVideoFrameRate(videoEl);
    const width = Number(videoEl.videoWidth || 0);
    const height = Number(videoEl.videoHeight || 0);
    const durationSec = Number.isFinite(videoEl.duration) ? Number(videoEl.duration) : null;
    const target = computeCappedVideoTarget({width, height});
    const oversizedByResolution = width > MAX_UPLOAD_WIDTH || height > MAX_UPLOAD_HEIGHT;
    const oversizedByFps =
      typeof fpsEstimate === "number" &&
      Number.isFinite(fpsEstimate) &&
      fpsEstimate > MAX_UPLOAD_FPS;
    const oversized = oversizedByResolution || oversizedByFps;

    return {
      width,
      height,
      durationSec,
      fpsEstimate,
      oversized,
      oversizedByResolution,
      oversizedByFps,
      targetWidth: target.width,
      targetHeight: target.height,
      targetFrameRate: MAX_UPLOAD_FPS,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function UploadVideoModal({ mapId: initialMapId, mapName: initialMapName, onClose, onVideoUploaded, teamId: teamIdOverride, initialSetupEventId = "" }) {
  const setupLifecycle = useEventSetupLifecycle();
  const [mapId, setSetupMapId] = useState(initialMapId);
  const [mapName, setSetupMapName] = useState(initialMapName);
  const [setupReady, setSetupReady] = useState(false);
  const { teamId: activeTeamId, username } = useTeam();
  const teamId = teamIdOverride || activeTeamId;

  // âœ… Video title removed (Match + POV identifies the VOD)
  // Focus points remain optional.
  const [focusPointsLoading, setFocusPointsLoading] = useState(false);
  const [focusPoints, setFocusPoints] = useState([]);
  const [file, setFile] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState(null);
  const [selectedVideoMeta, setSelectedVideoMeta] = useState(null);
  const [inspectingVideoMeta, setInspectingVideoMeta] = useState(false);

  const [selectedTags, setSelectedTags] = useState([]);
  const [alert, setAlert] = useState(null);

  // Team comp
  const [selectedComp, setSelectedComp] = useState(["", "", "", "", ""]);

  // POV name (for multi-POV match videos)
  const povOptions = REVIEW_POV_OPTIONS;
  const [povName, setPovName] = useState("Coach's POV");

  // Match selection
  // Default to "new" per your request
  const [matchMode, setMatchMode] = useState("new"); // "existing" | "new"
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [resolvedMatchId, setResolvedMatchId] = useState("");
  const [newMatchTitle, setNewMatchTitle] = useState("");
  const [selectedCalendarEventId, setSelectedCalendarEventId] = useState(initialSetupEventId);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);
  const [existingMatchPovNames, setExistingMatchPovNames] = useState([]);

  // Match Date/Time (when the match was actually played)
  const [matchDate, setMatchDate] = useState(""); // "YYYY-MM-DD"
  const [matchTime, setMatchTime] = useState(""); // "HH:MM"

  const submittingRef = useRef(false);
  const fileInspectionTokenRef = useRef(0);
  const reservationSessionIdRef = useRef(createMatchReservationSessionId());
  const activeReservationRef = useRef(null);

  // Sync marker
  const previewVideoRef = useRef(null);
  const [syncOffsetSec, setSyncOffsetSec] = useState(null);

  // NEW: Team-stats scoreboard frame capture
  const [scoreboardCaptureSec, setScoreboardCaptureSec] = useState(null);
  const [scoreboardFrameFile, setScoreboardFrameFile] = useState(null);
  const [openTeamStatsDialog, setOpenTeamStatsDialog] = useState(false);

  // NEW: pre-generated IDs so team-stats can link to the future VOD/match
  const [pendingVideoId, setPendingVideoId] = useState(null);
  const [pendingMatchId, setPendingMatchId] = useState(null);

  const [teamStatsDraft, setTeamStatsDraft] = useState(null);
  const [savedTeamStatsMatchId, setSavedTeamStatsMatchId] = useState(null);
  const [savedTeamStatsImagePath, setSavedTeamStatsImagePath] = useState(null);
  const reservationIdentity = useMemo(
    () => ({
      uid: getAuth().currentUser?.uid || "",
      username: username || "Unknown",
      sessionId: reservationSessionIdRef.current,
      surface: "web-upload",
    }),
    [username]
  );
  const selectedExistingMatch = useMemo(
    () => matches.find((entry) => entry.id === selectedMatchId) || null,
    [matches, selectedMatchId]
  );
  const povUsageTargetMatchId =
    matchMode === "existing" ? selectedMatchId : resolvedMatchId;
  const focusPointTargetMatchId = povUsageTargetMatchId;

  const resetTeamStatsModalUiOnly = () => {
    setOpenTeamStatsDialog(false);
  };

  const resetTeamStatsSession = () => {
    setScoreboardCaptureSec(null);
    setScoreboardFrameFile(null);
    setOpenTeamStatsDialog(false);
    setPendingVideoId(null);
    setPendingMatchId(null);
    setTeamStatsDraft(null);
    setSavedTeamStatsMatchId(null);
    setSavedTeamStatsImagePath(null);
  };

  const openTeamStatsFromPreview = async (capturedFile, capturedSeconds) => {
    try {
      const v = previewVideoRef.current;
      if (!v) return;

      // If we already have a captured frame for this upload session,
      // just reopen the dialog and keep the existing draft data.
      if (scoreboardFrameFile && !capturedFile) {
        setOpenTeamStatsDialog(true);
        return;
      }

      const { resolvedVideoId, resolvedMatchId } = await resolvePendingIdsForTeamStats();

      const frameFile = capturedFile || await captureVideoFrameFile(v);
      if (!frameFile) {
        setAlert({
          message: "Could not capture the scoreboard frame.",
          type: "error",
        });
        return;
      }

      setPendingVideoId(resolvedVideoId);
      setPendingMatchId(resolvedMatchId);
      setScoreboardCaptureSec(capturedSeconds ?? Number(v.currentTime.toFixed(3)));
      setScoreboardFrameFile(frameFile);
      setOpenTeamStatsDialog(true);
    } catch (err) {
      setAlert({
        message: err?.message || "Could not prepare team stats linking.",
        type: "error",
      });
    }
  };

  

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected || !selected.type.startsWith("video/")) {
      setSelectedVideoMeta(null);
      setAlert({ message: "Please select a valid video file.", type: "error" });
      e.target.value = "";
      return;
    }

    const token = Date.now();
    fileInspectionTokenRef.current = token;
    setFile(selected);
    setSelectedVideoMeta(null);
    setSyncOffsetSec(null);
    setInspectingVideoMeta(true);

    // New file = completely new upload session
    resetTeamStatsSession();

    try {
      const inspectedMeta = await inspectSelectedVideoFile(selected);
      if (fileInspectionTokenRef.current !== token) return;
      setSelectedVideoMeta(inspectedMeta);
    } catch (error) {
      if (fileInspectionTokenRef.current !== token) return;
      setFile(null);
      setSelectedVideoMeta(null);
      e.target.value = "";
      setAlert({
        message:
          error?.message ||
          "Could not inspect that video. Please choose a different file.",
        type: "error",
      });
    } finally {
      if (fileInspectionTokenRef.current === token) {
        setInspectingVideoMeta(false);
      }
    }
  };

  async function persistDraftFocusPoints({ nextMatchId, nextVideoId }) {
    const targetMatchId = String(nextMatchId || "").trim();
    const targetVideoId = String(nextVideoId || "").trim();

    if (!teamId || !mapId || (!targetMatchId && !targetVideoId)) {
      return;
    }

    const nextPoints = sanitizeModalFocusPoints(focusPoints);
    const focusPointsRef = targetMatchId
      ? collection(
          db,
          "teams",
          teamId,
          "maps",
          mapId,
          "matches",
          targetMatchId,
          "focusPoints"
        )
      : collection(
          db,
          "teams",
          teamId,
          "maps",
          mapId,
          "videos",
          targetVideoId,
          "focusPoints"
        );

    const existingPointsSnap = await getDocs(query(focusPointsRef, orderBy("order", "asc")));
    const batch = writeBatch(db);
    const existingIds = existingPointsSnap.docs.map((docSnap) => docSnap.id);
    const keptIds = nextPoints
      .map((point) => point.id)
      .filter((id) => typeof id === "string" && !id.startsWith("temp-"));

    existingIds
      .filter((id) => !keptIds.includes(id))
      .forEach((id) => batch.delete(doc(focusPointsRef, id)));

    nextPoints.forEach((point, index) => {
      if (String(point.id || "").startsWith("temp-")) {
        batch.set(doc(focusPointsRef), {
          order: index,
          text: point.text,
          ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
          ratings: normalizeStoredFocusPointRatings(point.ratings ?? point.rating),
          outcome: normalizeStoredFocusPointOutcome(point.outcome),
          outcomeNote: point.outcomeNote || "",
        });
        return;
      }

      batch.set(
        doc(focusPointsRef, point.id),
        {
          order: index,
          text: point.text,
          ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
          outcome: normalizeStoredFocusPointOutcome(point.outcome),
          outcomeNote: point.outcomeNote || "",
        },
        { merge: true }
      );
    });

    await batch.commit();
    setFocusPoints(nextPoints);
  }

  const videoPreviewURL = useMemo(() => {
    return file ? URL.createObjectURL(file) : null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (videoPreviewURL) URL.revokeObjectURL(videoPreviewURL);
    };
  }, [videoPreviewURL]);

  // Format seconds -> m:ss.mmm (ex: 390.889 -> 6:30.889)
  const formatSecondsToMinSec = (sec) => {
    const n = Number(sec);
    if (!Number.isFinite(n) || n < 0) return "";
    const minutes = Math.floor(n / 60);
    const seconds = n - minutes * 60;
    const secFixed = seconds.toFixed(3);
    const [secWhole, ms] = secFixed.split(".");
    const secPadded = String(secWhole).padStart(2, "0");
    return `${minutes}:${secPadded}.${ms}`;
  };

  // NEW: capture the currently displayed video frame as a PNG blob
  const captureVideoFrameFile = async (videoEl) => {
    if (!videoEl) return null;

    if (videoEl.readyState < 2) {
      await new Promise((resolve) => {
        const handleLoadedData = () => {
          videoEl.removeEventListener("loadeddata", handleLoadedData);
          resolve();
        };
        videoEl.addEventListener("loadeddata", handleLoadedData);
      });
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const width = videoEl.videoWidth || 1920;
    const height = videoEl.videoHeight || 1080;

    if (!width || !height) return null;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(videoEl, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((nextBlob) => resolve(nextBlob), "image/png");
    });

    if (!blob) return null;

    return new File([blob], `scoreboard-frame-${Date.now()}.png`, {
      type: "image/png",
    });
  };

  const resolvePendingIdsForTeamStats = async () => {
    if (!teamId || !mapId) {
      throw new Error("Missing team or map context.");
    }

    const videosColl = collection(db, "teams", teamId, "maps", mapId, "videos");

    // Reuse already-generated videoId if we already made one during this modal session
    let nextVideoId = pendingVideoId;
    if (!nextVideoId) {
      nextVideoId = doc(videosColl).id;
      setPendingVideoId(nextVideoId);
    }

    // Existing match mode: just use chosen match
    if (matchMode === "existing") {
      if (!selectedMatchId) {
        throw new Error("Please select an existing match first.");
      }

      setPendingMatchId(selectedMatchId);
      return {
        resolvedVideoId: nextVideoId,
        resolvedMatchId: selectedMatchId,
      };
    }

    // New match mode: resolve or pre-generate a stable match id
    const desiredTitle = newMatchTitle.trim();
    if (!desiredTitle) {
      throw new Error("Please enter a match title first.");
    }

    if (!matchDate || !matchTime) {
      throw new Error("Please set the Match Date and Match Time first.");
    }

    if (resolvedMatchId) {
      setPendingMatchId(resolvedMatchId);
      return {
        resolvedVideoId: nextVideoId,
        resolvedMatchId,
      };
    }

    if (!reservationIdentity.uid) {
      throw new Error("You must be signed in to prepare the shared Match ID.");
    }

    const reservation = await ensureMatchReservation({
        eventKey: selectedCalendarEventId,
      db,
      teamId,
      mapId,
      title: desiredTitle,
      matchDate,
      matchTime,
      tags: selectedTags,
      comp: selectedComp,
      identity: reservationIdentity,
    });

    if (reservation.claimActive) {
      activeReservationRef.current = {
        kind: "new",
        matchId: reservation.matchId,
        reservationKey: reservation.reservationKey,
      };
    }
    setResolvedMatchId(reservation.matchId);
    setPendingMatchId(reservation.matchId);

    return {
      resolvedVideoId: nextVideoId,
      resolvedMatchId: reservation.matchId,
    };
  };

  // Load recent matches for this map
  useEffect(() => {
    if (!teamId || !mapId) return;
    const matchesRef = collection(db, "teams", teamId, "maps", mapId, "matches");
    const qRef = query(matchesRef, orderBy("createdAt", "desc"), limit(30));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const uniqueById = Array.from(new Map(arr.map((m) => [m.id, m])).values());
        setMatches(uniqueById);

      },
      (e) => {
        console.error("Failed to load matches:", e);
      }
    );

    return unsub;
  }, [teamId, mapId]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    void cleanupStaleMatchReservations({
      db,
      teamId,
      mapId,
    }).catch(() => undefined);

    return undefined;
  }, [mapId, reservationIdentity.uid, teamId]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    let cancelled = false;
    const nextReservationKey =
      matchMode === "new" ? buildMatchReservationKey(newMatchTitle, matchDate, matchTime, selectedCalendarEventId) : "";
    const shouldClaimExistingReservedMatch =
      matchMode === "existing" &&
      !!selectedMatchId &&
      !!selectedExistingMatch &&
      isReservedMatchDoc(selectedExistingMatch);
    const hasSameReservation =
      !!activeReservationRef.current &&
      ((activeReservationRef.current.kind === "new" &&
        activeReservationRef.current.reservationKey === nextReservationKey) ||
        (activeReservationRef.current.kind === "existing" &&
          activeReservationRef.current.matchId === selectedMatchId));

    async function syncReservationSelection() {
      if (!hasSameReservation && activeReservationRef.current) {
        const activeReservation = activeReservationRef.current;
        activeReservationRef.current = null;
        await releaseMatchReservation({
          db,
          teamId,
          mapId,
          matchId: activeReservation.matchId,
          identity: reservationIdentity,
        }).catch(() => undefined);
      }
      if (cancelled) return;

      if (matchMode === "existing") {
        setResolvedMatchId(selectedMatchId || "");
        setPendingMatchId(selectedMatchId || null);

        if (!selectedMatchId || !shouldClaimExistingReservedMatch) {
          return;
        }

        const reservation = await claimReservedMatch({
          db,
          teamId,
          mapId,
          matchId: selectedMatchId,
          identity: reservationIdentity,
        });

        if (cancelled) {
          if (reservation.claimActive) {
            await releaseMatchReservation({
              db,
              teamId,
              mapId,
              matchId: reservation.matchId,
              identity: reservationIdentity,
            }).catch(() => undefined);
          }
          return;
        }

        activeReservationRef.current = reservation.claimActive
          ? {
              kind: "existing",
              matchId: reservation.matchId,
              reservationKey: reservation.reservationKey,
            }
          : null;
        return;
      }

      if (!nextReservationKey) {
        setResolvedMatchId("");
        setPendingMatchId(null);
        return;
      }

      const reservation = await ensureMatchReservation({
        eventKey: selectedCalendarEventId,
        db,
        teamId,
        mapId,
        title: newMatchTitle,
        matchDate,
        matchTime,
        tags: selectedTags,
        comp: selectedComp,
        identity: reservationIdentity,
      });

      if (cancelled) {
        if (reservation.claimActive) {
          await releaseMatchReservation({
            db,
            teamId,
            mapId,
            matchId: reservation.matchId,
            identity: reservationIdentity,
          }).catch(() => undefined);
        }
        return;
      }

      activeReservationRef.current = reservation.claimActive
        ? {
            kind: "new",
            matchId: reservation.matchId,
            reservationKey: reservation.reservationKey,
          }
        : null;
      setResolvedMatchId(reservation.matchId);
      setPendingMatchId(reservation.matchId);
    }

    void syncReservationSelection();

    return () => {
      cancelled = true;
    };
  }, [
    mapId,
    matchDate,
    matchMode,
    matchTime,
    newMatchTitle,
    selectedCalendarEventId,
    reservationIdentity,
    selectedComp,
    selectedExistingMatch,
    selectedMatchId,
    selectedTags,
    teamId,
  ]);

  useEffect(() => {
    if (!teamId || !mapId || !reservationIdentity.uid) return undefined;

    const heartbeatId = window.setInterval(() => {
      const activeReservation = activeReservationRef.current;
      if (!activeReservation) return;

      void refreshMatchReservationHeartbeat({
        db,
        teamId,
        mapId,
        matchId: activeReservation.matchId,
        identity: reservationIdentity,
      }).catch(() => undefined);
    }, 60_000);

    return () => {
      window.clearInterval(heartbeatId);
    };
  }, [mapId, reservationIdentity, teamId]);

  useEffect(() => {
    return () => {
      const activeReservation = activeReservationRef.current;
      activeReservationRef.current = null;
      if (!activeReservation || !reservationIdentity.uid) return;

      void releaseMatchReservation({
        db,
        teamId,
        mapId,
        matchId: activeReservation.matchId,
        identity: reservationIdentity,
      }).catch(() => undefined);
    };
  }, [mapId, reservationIdentity, teamId]);

  // âœ… Auto-fill tags + comp when selecting an existing match (if match doc has them)
  useEffect(() => {
    if (matchMode !== "existing") return;
    if (!selectedMatchId) return;

    const m = matches.find((x) => x.id === selectedMatchId);
    if (!m) return;

    if (Array.isArray(m.tags)) setSelectedTags(m.tags);

    if (Array.isArray(m.comp)) {
      const next = ["", "", "", "", ""];
      m.comp.slice(0, 5).forEach((a, idx) => (next[idx] = a));
      setSelectedComp(next);
    }
  }, [matchMode, selectedMatchId, matches]);

  useEffect(() => {
    let cancelled = false;

    async function loadExistingMatchPovNames() {
      if (!teamId || !mapId || !povUsageTargetMatchId) {
        setExistingMatchPovNames([]);
        return;
      }

      try {
        const existingPovsSnap = await getDocs(
          query(
            collection(db, "teams", teamId, "maps", mapId, "videos"),
            where("matchId", "==", povUsageTargetMatchId)
          )
        );

        if (cancelled) return;
        setExistingMatchPovNames(extractUniquePovNamesFromSnapshot(existingPovsSnap));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load existing POV names:", error);
        setExistingMatchPovNames([]);
      }
    }

    void loadExistingMatchPovNames();

    return () => {
      cancelled = true;
    };
  }, [mapId, povUsageTargetMatchId, teamId]);

  useLiveSetupFocusPoints({teamId, mapId, matchId: focusPointTargetMatchId,
    eventId: selectedCalendarEventId, event: selectedCalendarEvent,
    selectionKey: `${matchMode}:${selectedMatchId}`, disabled: uploading,
    setFocusPoints, setLoading: setFocusPointsLoading, normalize: cloneModalFocusPoints,
    onError: error => {
      console.error("[UploadVideoModal] Could not update event focus points:", error);
      setAlert({type: "error", message: "Could not update event focus points. Check your connection and select the event again to retry."});
    },
  });

  const existingMatchPovNameSet = useMemo(() => {
    return new Set(existingMatchPovNames.map(normalizePovName).filter(Boolean));
  }, [existingMatchPovNames]);

  const cleanupSavedTeamStatsIfUploadCancelled = async () => {
    // Saved stats belong to the event, even if this particular POV is cancelled.
    if (selectedCalendarEventId?.startsWith("setup:")) return;
    if (!teamId || !savedTeamStatsMatchId) return;

    try {
      if (savedTeamStatsImagePath) {
        try {
          const storageObjRef = ref(getStorage(), savedTeamStatsImagePath);
          await deleteObject(storageObjRef);
        } catch (storageErr) {
          console.warn("Failed to delete saved team-stats image:", storageErr);
        }
      }

      await deleteDoc(doc(db, "teams", teamId, "matches", savedTeamStatsMatchId));
    } catch (err) {
      console.warn("Failed to clean up saved team stats after cancelling upload:", err);
    } finally {
      resetTeamStatsSession();
    }
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return; // hard lock
    if (focusPointsLoading) {setAlert({message: "Wait for the event focus points to finish loading.", type: "error"}); return;}
    if (!setupReady) {setAlert({message: "Save and select your event setup before uploading.", type: "error"}); return;}
    submittingRef.current = true;

    const auth = getAuth();
    const uid = auth.currentUser?.uid || null;

    if (!uid) {
      setAlert({ message: "You must be signed in to upload a video.", type: "error" });
      submittingRef.current = false;
      return;
    }
    if (!teamId || !mapId) {
      setAlert({ message: "Missing team or map context.", type: "error" });
      submittingRef.current = false;
      return;
    }

    // âœ… Video title removed; only file required
    if (!file) {
      setAlert({ message: "Please select a video.", type: "error" });
      submittingRef.current = false;
      return;
    }
    if (inspectingVideoMeta) {
      setAlert({
        message: "Video details are still being inspected. Please wait a moment.",
        type: "error",
      });
      submittingRef.current = false;
      return;
    }

    if (matchMode === "existing" && !selectedMatchId) {
      setAlert({ message: "Select an event and map slot in Setup.", type: "error" });
      submittingRef.current = false;
      return;
    }
    if (matchMode === "new" && !newMatchTitle.trim()) {
      setAlert({ message: "Complete and save the event details in Setup.", type: "error" });
      submittingRef.current = false;
      return;
    }
    if (matchMode === "new" && (!matchDate || !matchTime)) {
      setAlert({ message: "Complete and save the event schedule in Setup.", type: "error" });
      submittingRef.current = false;
      return;
    }
    if (matchMode === "new" && !resolvedMatchId) {
      setAlert({
        message: "Your event is still being prepared. Please wait a moment.",
        type: "error",
      });
      submittingRef.current = false;
      return;
    }
    if (syncOffsetSec === null) {
      setAlert({
        message: "Please mark the sync point (Round 1 Pre-round â€“ 0:01).",
        type: "error",
      });
      submittingRef.current = false;
      return;
    }

    let scaffoldId = null;
    try {
      await readSetupSelection(teamId, selectedCalendarEventId, {requireMap: true, mapId});
      setUploading(true);
      setUploadProgress(0);
      setEstimatedTimeLeft(null);
      scaffoldId = pendingVideoId || doc(collection(db, "teams", teamId, "maps", mapId, "videos")).id;
      updatePendingVodUpload(scaffoldId, {teamId, mapId, title: newMatchTitle.trim() || selectedExistingMatch?.title || "New VOD", povName, status: "uploading", progress: 0});

      const allowance = await checkTeamQuotaAllowance({
        teamId,
        action: "upload_video",
        bytesToAdd: file.size || 0,
      });

      if (!allowance?.uploadAllowed) {
        removePendingVodUpload(scaffoldId);
        setAlert({
          message: allowance?.message || "Uploading is unavailable right now.",
          type: "error",
        });
        setUploading(false);
        submittingRef.current = false;
        return;
      }

      await ensureActiveTeamClaim(teamId);
      await getAuth().currentUser.getIdToken(true);

      const cleanComp = selectedComp.filter((a) => a);
      const isOversizedUpload = !!selectedVideoMeta?.oversized;

      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      // 0) Determine matchId (WITHOUT writing yet)
      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      const desiredTitle = newMatchTitle.trim();
      const matchKey = desiredTitle.toLowerCase().replace(/\s+/g, " ").trim();
      const matchPlayedAt =
        matchMode === "new"
          ? Timestamp.fromDate(new Date(`${matchDate}T${matchTime}:00`))
          : null;
      let matchIdToUse = matchMode === "new" ? resolvedMatchId : selectedMatchId;

      const linkedEventId = selectedCalendarEventId || selectedExistingMatch?.calendarEventId || null;
      const plannedMapName = String(selectedCalendarEvent?.mapName || "").trim();
      const currentMapName = String(mapName || "").trim();
      let allowCalendarMapChange = false;
      if (
        plannedMapName
        && currentMapName
        && plannedMapName.toLowerCase() !== currentMapName.toLowerCase()
      ) {
        const confirmed = window.confirm(
          `Change the linked event map from ${plannedMapName} to ${currentMapName}? This updates the calendar event and session plan. Map-specific focus points will be marked for review.`,
        );
        if (!confirmed) {
          throw new Error("Map change was cancelled. Upload was not started.");
        }
        allowCalendarMapChange = true;
      }
      if (selectedCalendarEvent?.matchId && selectedCalendarEvent.matchId !== matchIdToUse) {
        throw new Error("The selected calendar event is already linked to a different match.");
      }
      const matchTitleToUse = matchMode === "new"
        ? desiredTitle
        : selectedExistingMatch?.title || "Match";

      const activeReservation = activeReservationRef.current;
      if (activeReservation?.matchId && activeReservation.matchId === matchIdToUse) {
        await markMatchReservationStarted({
          db,
          identity: reservationIdentity,
          teamId,
          mapId,
          matchId: activeReservation.matchId,
        });
        activeReservationRef.current = null;
      }

      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      // 1) Create video doc ID (WITHOUT writing yet)
      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      const videosColl = collection(db, "teams", teamId, "maps", mapId, "videos");
      const videoRef = doc(videosColl, scaffoldId);
      const videoId = videoRef.id;
      scaffoldId = videoId;
      updatePendingVodUpload(videoId, {teamId, mapId, title: matchTitleToUse, povName, status: "uploading", progress: 0});

      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      // 2) Upload to Storage (first), using the stable videoId
      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      const storage = getStorage();
      const safeName = file.name
        .replace(/\s+/g, "_")
        .replace(/[/\\#?]/g, "_")
        .trim();
      const directUploadFinalPath = `videos/${teamId}/${mapId}/${videoId}/${safeName}`;
      const processedFinalPath = `videos/${teamId}/${mapId}/${videoId}/playback.mp4`;
      const sourcePath = `${SOURCE_UPLOAD_PREFIX}/${teamId}/${mapId}/${videoId}/${safeName}`;
      const renderOutputPrefix = `${RENDER_OUTPUT_PREFIX}/${teamId}/${mapId}/${videoId}/`;
      const renderOutputObjectPath = `${renderOutputPrefix}playback.mp4`;
      const uploadPath = isOversizedUpload ? sourcePath : directUploadFinalPath;
      const storageRefObj = ref(storage, uploadPath);

      const uploadTask = uploadBytesResumable(storageRefObj, file, {
        contentType: file.type || "video/mp4",
        cacheControl: "public, max-age=31536000, immutable",
        contentDisposition: `inline; filename="${file.name}"`,
        customMetadata: {
          teamId,
          mapId,
          videoDocId: videoId,
          createdBy: username || "",
          createdByUid: uid || "",
          matchId: matchIdToUse || "",
          povName: povName || "",
          processingManaged: isOversizedUpload ? "true" : "false",
          sourceUpload: isOversizedUpload ? "true" : "false",
        },
      });

      const startTime = Date.now();

      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(Number(progress.toFixed(1)));
            updatePendingVodUpload(videoId, {progress});

            const elapsed = (Date.now() - startTime) / 1000;
            let timeLeft = null;

            if (snapshot.bytesTransferred > 0) {
              const total = (elapsed / snapshot.bytesTransferred) * snapshot.totalBytes;
              timeLeft = Math.max(0, total - elapsed);
            }

            if (timeLeft !== null && timeLeft >= 60) {
              const m = Math.floor(timeLeft / 60);
              const s = Math.floor(timeLeft % 60);
              setEstimatedTimeLeft(`${m}m ${s}s`);
            } else if (timeLeft !== null) {
              setEstimatedTimeLeft(`${Math.ceil(timeLeft)}s`);
            } else {
              setEstimatedTimeLeft(null);
            }
          },
          reject,
          () => resolve()
        );
      });

      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      // 3) Write Firestore docs AFTER upload succeeds
      // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      updatePendingVodUpload(videoId, {status: "finalizing", progress: 100});
      const calendarLink = await linkMatchToCalendarEvent({
        teamId,
        mapId,
        mapName,
        matchId: matchIdToUse,
        matchTitle: matchTitleToUse,
        playedAt: matchPlayedAt || selectedExistingMatch?.matchPlayedAt || new Date(),
        actor: {
          uid,
          username: username || auth.currentUser?.email || "Member",
        },
        calendarEventId: linkedEventId,
        allowMapChange: allowCalendarMapChange,
      });
      setSelectedCalendarEventId(calendarLink.event.eventId);
      setSelectedCalendarEvent(calendarLink.event);

      const batch = writeBatch(db);

      if (matchMode === "new" && matchIdToUse) {
        batch.set(
          doc(db, "teams", teamId, "maps", mapId, "matches", matchIdToUse),
          {
            title: calendarLink.event.title || desiredTitle,
            matchKey,
            matchPlayedAt,
            tags: selectedTags,
            comp: cleanComp,
            ...(savedTeamStatsMatchId
              ? {
                  teamStatsMatchId: savedTeamStatsMatchId,
                  teamStatsImageUrl: null,
                  teamStatsImageStoragePath: savedTeamStatsImagePath || null,
                  teamStatsLinkedAt: serverTimestamp(),
                }
              : {}),
          },
          { merge: true }
        );
      }

      const commonVideoDoc = {
        povName,
        videoURL: null,
        createdBy: username || "Unknown",
        createdByUid: uid,
        createdAt: Timestamp.now(),
        tags: selectedTags,
        comp: cleanComp,
        matchId: matchIdToUse,
        calendarEventId: calendarLink.event.eventId,
        sessionPlanId: calendarLink.event.sessionPlanId || null,
        syncOffsetSec: syncOffsetSec,
        ...(savedTeamStatsMatchId
          ? {
              teamStatsMatchId: savedTeamStatsMatchId,
              teamStatsImageUrl: null,
              teamStatsImageStoragePath: savedTeamStatsImagePath || null,
              teamStatsLinkedAt: serverTimestamp(),
            }
          : {}),
      };

      if (isOversizedUpload) {
        const processingEstimateSeconds = estimateProcessingSecondsForUpload(
          selectedVideoMeta,
          uploadTask.snapshot.totalBytes || file.size || 0
        );

        batch.set(videoRef, {
          ...commonVideoDoc,
          status: "processing",
          storagePath: null,
          sizeBytes: null,
          sourceBytes: uploadTask.snapshot.totalBytes ?? null,
          sourcePath,
          finalStoragePath: processedFinalPath,
          renderOutputPrefix,
          renderOutputObjectPath,
          processingMode: "oversized_transcode",
          processingStage: "queued",
          processingProgressPercent: 8,
          processingMessage:
            "This upload exceeds the 1080p60 playback target and is being optimized before teammates can watch it.",
          processingQueuedAt: Timestamp.now(),
          processingEstimateSeconds,
          processingEtaAt: Timestamp.fromMillis(
            Date.now() + (processingEstimateSeconds * 1000)
          ),
          sourceWidth: Number(selectedVideoMeta?.width || 0) || null,
          sourceHeight: Number(selectedVideoMeta?.height || 0) || null,
          sourceDurationSec:
            Number(selectedVideoMeta?.durationSec || 0) || null,
          sourceFpsEstimate:
            Number(selectedVideoMeta?.fpsEstimate || 0) || null,
          processingTargetWidth:
            Number(selectedVideoMeta?.targetWidth || 0) || null,
          processingTargetHeight:
            Number(selectedVideoMeta?.targetHeight || 0) || null,
          processingTargetFrameRate: MAX_UPLOAD_FPS,
        });
      } else {
        const finalization = await confirmTrackedUploadFinalize({
          teamId,
          objectPath: directUploadFinalPath,
        });

        if (!finalization?.accepted) {
          throw new Error(
            finalization?.message ||
              "Upload was rejected by the current team quota rules."
          );
        }

        batch.set(videoRef, {
          ...commonVideoDoc,
          storagePath: directUploadFinalPath,
          sizeBytes: uploadTask.snapshot.totalBytes ?? null,
          sourceBytes: uploadTask.snapshot.totalBytes ?? null,
          status: "ready",
          sourceWidth: Number(selectedVideoMeta?.width || 0) || null,
          sourceHeight: Number(selectedVideoMeta?.height || 0) || null,
          sourceDurationSec:
            Number(selectedVideoMeta?.durationSec || 0) || null,
          sourceFpsEstimate:
            Number(selectedVideoMeta?.fpsEstimate || 0) || null,
          transcodedAt: serverTimestamp(),
        });
      }

      await batch.commit();
      await persistDraftFocusPoints({
        nextMatchId: matchIdToUse,
        nextVideoId: videoId,
      });


      triggerTeamQuotaRefresh({
        teamId,
        bytesDelta: Number(uploadTask.snapshot.totalBytes || file.size || 0),
      });

      if (isOversizedUpload) {
        try {
          await beginOversizedVideoProcessing({
            teamId,
            mapId,
            videoId,
          });
        } catch (processingError) {
          console.error(
            "[UploadVideoModal] Failed to begin oversized video processing:",
            processingError
          );
        }
      }

      onVideoUploaded?.();
      onClose?.();
    } catch (err) {
      removePendingVodUpload(scaffoldId);
      console.error("Upload failed:", err);
      if(!String(err?.code || '').includes('cancel'))void reportOperationNotification({teamId,type:'uploadAttention',operationId:scaffoldId,message:'VOD upload failed: '+(err?.message || 'Open the uploader to retry.')});
      setAlert({
        message: err?.message || "Failed to upload video. Please try again.",
        type: "error",
      });
    } finally {
      setUploading(false);
      submittingRef.current = false;
    }
  };


  const handleCalendarEventSelection = (eventId, event) => {
    setSelectedCalendarEventId(eventId || "");
    setSelectedCalendarEvent(event || null);
    setSelectedComp(Array.isArray(event?.comp) ? event.comp : []);

    if (!event) {
      setMatchMode("new");
      return;
    }

    setSetupMapId(event.mapId || "");
    setSetupMapName(event.mapName || event.mapId || "");
    const schedule = toLocalDateTimeInputs(event.startUTC);
    if (schedule.date) setMatchDate(schedule.date);
    if (schedule.time) setMatchTime(schedule.time);
    setNewMatchTitle(String(event.note || event.title || event.tag || "Team match").trim());

    if (event.matchId) {
      setMatchMode("existing");
      setSelectedMatchId(event.matchId);
      return;
    }

    setMatchMode("new");
    setSelectedMatchId("");
  };



  const handleModalClose = async () => {
    if (uploading) return;
    try {
      await cleanupSavedTeamStatsIfUploadCancelled();
    } finally {
      onClose?.();
    }
  };

  const todayISO = useMemo(() => getCurrentLocalDateInputValue(), []);
  const stableLinkedVideoMeta = useMemo(
    () => ({
      mapId: mapId || "",
      videoId: pendingVideoId || "",
      matchId: pendingMatchId || "",
      scoreboardCaptureSec:
        typeof scoreboardCaptureSec === "number" ? scoreboardCaptureSec : null,
      syncOffsetSec:
        typeof syncOffsetSec === "number" ? Number(syncOffsetSec.toFixed(3)) : null,
      comp: selectedComp.filter(Boolean),
    }),
    [
      mapId,
      pendingMatchId,
      pendingVideoId,
      scoreboardCaptureSec,
      selectedComp,
      syncOffsetSec,
    ]
  );

  return (
    <>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          position="top"
          onClose={() => setAlert(null)}
        />
      )}

      <div
        className="upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Upload video"
      >
        <div className={`modal-content ${file ? "has-video" : ""}`}>
          <div className="modal-header">
            <h3>Upload New VOD</h3>
            <button
              className="close-button app-cancel-control"
              onClick={() => void handleModalClose()}
              aria-label="Close"
              disabled={uploading}
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className="uvm-pov-block">
              <p className="uvm-section-title">Match Assignment</p>
              <CalendarMatchLinker
                lifecycle={setupLifecycle}
                showUnplannedHint={false}
                teamId={teamId}
                mapId={mapId}
                mapName={mapName}
                onReadyChange={setSetupReady}
                selectedEventId={selectedCalendarEventId}
                lockSelection={!!initialSetupEventId}
                onSelectEvent={handleCalendarEventSelection}
                disabled={uploading}
              />
              {povUsageTargetMatchId && existingMatchPovNames.length > 0 ? (
                <p className="uvm-sync-readout">
                  Existing POVs: <b>{existingMatchPovNames.join(", ")}</b>
                </p>
              ) : null}
            </div>

            <div className="uvm-pov-block">
              <p className="uvm-section-title">POV Name</p>
              <select
                value={povName}
                onChange={(e) => setPovName(e.target.value)}
                disabled={uploading}
              >
                {povOptions.map((option) => (
                  <option key={option} value={option}>
                    {buildCloudPovOptionLabel(
                      option,
                      existingMatchPovNameSet.has(normalizePovName(option))
                    )}
                  </option>
                ))}
              </select>
            </div>

            <div className="uvm-preparation-grid">
              <div className="uvm-pov-block">
                <FocusPoints
                  mapId={mapId}
                  teamId={teamId}
                  matchId={focusPointTargetMatchId}
                  videoId=""
                  focusPoints={focusPoints}
                  onDraftChange={setFocusPoints}
                  alwaysEditable
                  instantDraft
                  returnToLibrary
                  showEmptyPlaceholder
                  showRatings={false}
                  disabled={uploading}
                />
            </div>

              </div>
            

            <label className="custom-file-upload">
              Choose Video File
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                hidden
                disabled={uploading}
              />
            </label>

            <p className="uvm-transcode-note">
              {inspectingVideoMeta
                ? "Inspecting video details..."
                : selectedVideoMeta?.oversized
                ? `This upload will keep the source file, then generate a cloud review copy capped to ${selectedVideoMeta.targetWidth}x${selectedVideoMeta.targetHeight} at ${MAX_UPLOAD_FPS}fps.`
                : "This upload already fits the 1080p / 60fps cloud playback target."}
            </p>

            {selectedVideoMeta ? (
              <p className="uvm-sync-readout">
                Source:{" "}
                <b>
                  {selectedVideoMeta.width}x{selectedVideoMeta.height}
                </b>
                {typeof selectedVideoMeta.fpsEstimate === "number" ? (
                  <span className="uvm-sync-seconds">
                    {selectedVideoMeta.fpsEstimate.toFixed(2)}fps
                  </span>
                ) : null}
                {typeof selectedVideoMeta.durationSec === "number" ? (
                  <span className="uvm-sync-seconds">
                    {formatSecondsToMinSec(selectedVideoMeta.durationSec)}
                  </span>
                ) : null}
              </p>
            ) : null}

            {file && (
              <>
                <p className="file-name">Video: {file.name}</p>
                <VodMarkerPicker src={videoPreviewURL} videoRef={previewVideoRef} syncOffsetSec={syncOffsetSec} scoreboardCaptureSec={scoreboardCaptureSec} fps={selectedVideoMeta?.fpsEstimate} disabled={uploading} onMarkSync={seconds=>setSyncOffsetSec(seconds)} onCaptureStats={openTeamStatsFromPreview} hasStats={!!scoreboardFrameFile} onReopenStats={()=>void openTeamStatsFromPreview()}/>
              </>
            )}

            {uploading && (
              <div className="upload-progress-wrapper">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p>{uploadProgress}% uploaded</p>
                {estimatedTimeLeft && <p>Estimated time left: {estimatedTimeLeft}</p>}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button onClick={() => void handleSubmit()} disabled={uploading || !file}>
              {uploading ? "Uploading..." : "Upload"}
            </button>
            <button
              className="uvm-cancel-btn app-cancel-control"
              onClick={() => void handleModalClose()}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {openTeamStatsDialog && scoreboardFrameFile && (
        <UploadScoreDialog
          initialSetupEventId={selectedCalendarEventId}
          savedStatsId={savedTeamStatsMatchId}
          key={`${pendingVideoId || "pending-video"}-${scoreboardCaptureSec || "no-time"}`}
          teamId={teamId}
          onClose={resetTeamStatsModalUiOnly}
          hideFileInput={true}
          initialImageBlob={scoreboardFrameFile}
          referenceVideoUrl={videoPreviewURL}
          initialMatchDateISO={matchDate || todayISO}
          disableImageUpload={true}
          draftState={teamStatsDraft}
          onDraftChange={setTeamStatsDraft}
          linkedVideoMeta={stableLinkedVideoMeta}
          onSaved={(savedMatch) => {
            setSavedTeamStatsMatchId(savedMatch?.id || null);
            setSavedTeamStatsImagePath(savedMatch?.imageStoragePath || null);
            setOpenTeamStatsDialog(false);
            setAlert({
              message:
                "Team stats saved from the captured scoreboard frame and linked automatically.",
              type: "success",
            });
          }}
        />
      )}
    </>
  );
}

function normalizeStoredFocusPointRatings(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((ratings, [username, rawRating]) => {
    const normalizedUsername = String(username || "").trim();
    const normalizedRating = Number(rawRating);

    if (
      !normalizedUsername ||
      !Number.isFinite(normalizedRating) ||
      normalizedRating < 1 ||
      normalizedRating > 5
    ) {
      return ratings;
    }

    ratings[normalizedUsername] = Math.round(normalizedRating);
    return ratings;
  }, {});
}

function normalizeStoredFocusPointOutcome(value) {
  return ["not_attempted", "improving", "achieved", "needs_rework"].includes(value)
    ? value
    : "not_attempted";
}

function cloneModalFocusPoints(points) {
  return Array.isArray(points)
    ? points.map((point) => ({
        ...point,
        id: point?.id || `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text: typeof point?.text === "string" ? point.text : "",
        ratings: normalizeStoredFocusPointRatings(point?.ratings ?? point?.rating),
        outcome: normalizeStoredFocusPointOutcome(point?.outcome),
        outcomeNote: typeof point?.outcomeNote === "string" ? point.outcomeNote : "",
        ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
      }))
    : [];
}

function sanitizeModalFocusPoints(points) {
  return cloneModalFocusPoints(points)
    .map((point) => ({
      ...point,
      text: point.text.trim(),
          ...noteClassification(point),
          sourceType:point.sourceType || "improvement_note",sourceId:point.sourceId || null,sourceScope:point.sourceScope || point.scope || "general",sourceMapId:point.sourceMapId || point.mapId || null,
    }))
    .filter((point) => point.text.length > 0);
}
